import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { LoginPage } from "@/routes/Login";
import { OverviewPage } from "@/routes/Overview";
import { LogsPage } from "@/routes/Logs";
import { TracesPage } from "@/routes/Traces";
import { ZeitstrahlPage } from "@/routes/Zeitstrahl";
import { FacilitiesPage } from "@/routes/Facilities";
import { SourcesPage } from "@/routes/Sources";
import { AlertingPage } from "@/routes/Alerting";
import { BerichtePage } from "@/routes/Berichte";
import { SettingsPage } from "@/routes/Settings";
import { useAuth } from "@/hooks/useAuth";
import { internesZiel } from "@/lib/ziel";

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { token } = useAuth();
  const location = useLocation();
  if (!token) {
    return (
      <Navigate to="/login" replace state={{ from: internesZiel(location.pathname + location.search) }} />
    );
  }
  return <>{children}</>;
}

/** Sperrt Admin-Bereiche für Viewer; redirected zu /logs. */
function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { isAdmin } = useAuth();
  if (!isAdmin) return <Navigate to="/logs" replace />;
  return <>{children}</>;
}

/**
 * Default-Route: leitet `/` → `/logs` und übernimmt dabei den Query-String.
 * Damit funktionieren alte Deeplinks wie `/?client=qrmaint&level=ERROR`
 * unverändert (die SPA landet auf `/logs?client=qrmaint&level=ERROR`).
 */
function IndexRedirect() {
  const location = useLocation();
  return <Navigate to={{ pathname: "/logs", search: location.search }} replace />;
}

export default function App() {
  return (
    <Routes>
      <Route path="login" element={<LoginPage />} />
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<IndexRedirect />} />
        <Route path="uebersicht" element={<OverviewPage />} />
        <Route path="logs" element={<LogsPage />} />
        <Route path="traces/*" element={<TracesPage />} />
        <Route path="zeitstrahl/*" element={<ZeitstrahlPage />} />
        <Route path="anlagen/*" element={<FacilitiesPage />} />
        <Route path="berichte" element={<BerichtePage />} />
        <Route
          path="quellen/*"
          element={
            <RequireAdmin>
              <SourcesPage />
            </RequireAdmin>
          }
        />
        <Route
          path="alarmierung"
          element={
            <RequireAdmin>
              <AlertingPage />
            </RequireAdmin>
          }
        />
        <Route
          path="einstellungen/*"
          element={
            <RequireAdmin>
              <SettingsPage />
            </RequireAdmin>
          }
        />
        <Route path="*" element={<Navigate to="/logs" replace />} />
      </Route>
    </Routes>
  );
}
